import { type Dtos } from "@inrev/common";
import {
	useAdminFetchAccount,
	useAdminFetchAccountWorkInProgressStatements,
} from "@portal/api/account/admin";
import { WipWorkspace } from "@portal/components/features/wip/wip-workspace";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { ErrorDisplay } from "@portal/components/ui/ErrorDisplay";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import type { FC } from "react";
import type { AccountTabProps } from "../../../AccountLayout";
import { AdminAccountWIPSection } from "./AdminAccountWIPSection";

const EMPTY_WIP_EXTRACTIONS: Dtos.Wip.Extraction.Get.Response[] = [];
const EMPTY_WIP_LINE_ITEMS: Dtos.Wip.LineItem.Get.Response[] = [];
const EMPTY_WIP_PROJECTS: Dtos.Wip.Project.Get.Response[] = [];
const EMPTY_WIP_STATEMENTS: Dtos.Wip.Statement.Get.Response[] = [];

type AdminAccountWIPTabProps = AccountTabProps;

export const AdminAccountWIPTab: FC<AdminAccountWIPTabProps> = ({ accountId }) => {
	const wipExtractionEnabled = useFeatureFlag("wip_extraction_project");
	const { account, accountError, accountIsLoading } = useAdminFetchAccount(
		wipExtractionEnabled ? undefined : accountId,
	);
	const { wipStatements, wipStatementsError, wipStatementsIsLoading } =
		useAdminFetchAccountWorkInProgressStatements(accountId);

	if (wipExtractionEnabled) {
		return (
			<WipWorkspace
				accountId={accountId}
				extractions={EMPTY_WIP_EXTRACTIONS}
				lineItems={EMPTY_WIP_LINE_ITEMS}
				projects={EMPTY_WIP_PROJECTS}
				statements={EMPTY_WIP_STATEMENTS}
			/>
		);
	}

	const error = accountError ?? wipStatementsError;

	return (
		<div className="flex flex-col space-y-5 pb-[125px]">
			<div className="w-full flex flex-col space-y-5">
				{error ? (
					<ErrorDisplay error={error} />
				) : !account || !wipStatements || accountIsLoading || wipStatementsIsLoading ? (
					<CenteredSpinner />
				) : (
					<AdminAccountWIPSection account={account} wipStatements={wipStatements} />
				)}
			</div>
		</div>
	);
};
